import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { SetOnboardingModal } from '@app/core/states/onboarding.actions';
import { OnboardingState } from '@app/core/states/onboarding.state';
import { DropdownOption } from '@app/ui/components/dropdown/dropdown.model';
import { ImageUploadComponent } from '@app/ui/components/image-upload/image-upload.component';
import { UIState } from '@app/ui/states/ui.state';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import {
  AddConfigAsset,
  RemoveImage,
  UpdateAppConfig,
} from '../../states/app-configuration.actions';
import { AppConfigurationState } from '../../states/app-configuration.state';

@Component({
  selector: 'rk-general-configuration',
  templateUrl: './general-configuration.component.html',
  styleUrls: ['./general-configuration.component.scss'],
  standalone: false,
})
export class GeneralConfigurationComponent implements OnInit, OnDestroy {
  @Select(AppConfigurationState.currentAppConfigLanguage)
  currentAppConfigLanguage$: Observable<string>;

  @Select(AppConfigurationState.currentAppConfigHeaderTextColor)
  currentAppConfigHeaderTextColor$: Observable<string>;

  @Select(AppConfigurationState.currentAppConfigBackgroundImage)
  currentAppConfigBackgroundImage$: Observable<string>;

  @Select(AppConfigurationState.currentAppConfigBackgroundImageLong)
  currentAppConfigBackgroundImageLong$: Observable<string>;

  @Select(AppConfigurationState.currentAppConfigShowBlurredCover)
  currentAppConfigShowBlurredCover$: Observable<boolean>;

  @Select(AppConfigurationState.appBackgroundIsBlurred)
  appBackgroundIsBlurred$: Observable<boolean>;

  @Select(AppConfigurationState.showHeaderLogo)
  showHeaderLogo$: Observable<boolean>;

  @Select(AppConfigurationState.backgroundOption)
  backgroundOption$: Observable<string>;

  @Select(AppConfigurationState.allStreamsHaveLogo)
  allStreamsHaveLogo$: Observable<boolean>;

  @Select(OnboardingState.currentStep)
  onboardingCurrentStep$: Observable<number>;

  @Select(UIState.isCreationProcess)
  isCreationProcess$: Observable<boolean>;

  @ViewChild('interface', { static: true }) interfaceRef!: ElementRef;

  private readonly subscriptions: Subscription = new Subscription();

  @ViewChild('backgroundImageUploadComponent')
  backgroundImageUploadComponent: ImageUploadComponent;

  @ViewChild('backgroundImageLongUploadComponent')
  backgroundImageLongUploadComponent: ImageUploadComponent;

  selectedLanguage: DropdownOption;
  backgroundColor$: Observable<string>;
  showImageUpload = true;

  constructor(private readonly store: Store) {}

  languageOptions: DropdownOption[] = [
    {
      value: 'en',
      label: '🇬🇧 English',
      disabled: false,
    },
    {
      value: 'fr',
      label: '🇫🇷 Français',
      disabled: false,
    },
    {
      value: 'de',
      label: '🇩🇪 Deutsch',
      disabled: false,
    },
    {
      value: 'es',
      label: '🇪🇸 Español',
      disabled: false,
    },
    {
      value: 'it',
      label: '🇮🇹 Italiano',
      disabled: false,
    },
    {
      value: 'pt',
      label: '🇵🇹 Português',
      disabled: false,
    },
    // {
    //   value: 'ru',
    //   label: '🇷🇺 Русский',
    //   disabled: false,
    // },
    // {
    //   value: 'ro',
    //   label: '🇷🇴 Română',
    //   disabled: false,
    // },
  ];

  ngOnInit() {
    // sets current language
    this.subscriptions.add(
      this.currentAppConfigLanguage$.subscribe((language: string) => {
        if (language) {
          this.selectedLanguage =
            this.languageOptions.find(option => option.value === language) ||
            this.languageOptions[0];
        }
      }),
    );

    this.subscriptions.add(
      combineLatest([this.onboardingCurrentStep$, this.isCreationProcess$]).subscribe(
        ([onboardingCurrentStep, isCreationProcess]) => {
          if (onboardingCurrentStep === 4 && isCreationProcess) {
            this.store.dispatch(
              new SetOnboardingModal('ModalInterfaceCustomization', true),
            );
          }
        },
      ),
    );
  }

  onBackgroundTypeChange(event: MatButtonToggleChange) {
    this.store.dispatch(
      new UpdateAppConfig(
        ['onglets', '0', 'showBlurredCover'],
        event.value === 'cover' ? 'YES' : 'NO',
      ),
    );

    this.store.dispatch(
      new UpdateAppConfig(['onglets', '0', 'backgroundOption'], event.value),
    );
  }

  onLanguageSelectionChange(selectedLanguage: string) {
    this.store.dispatch(new UpdateAppConfig(['lang'], selectedLanguage));
  }

  onColorChange(color: string, key: string) {
    this.store.dispatch(new UpdateAppConfig(['onglets', '0', key], color));
  }

  onBackgroundImageSelected(event: any) {
    const image = event.file;
    const displayImageUrl = event.imageUrl;
    this.store.dispatch(new AddConfigAsset('backgroundappfield', image, displayImageUrl));
  }

  onBackgroundImageLongSelected(event: any) {
    const image = event.file;
    const displayImageUrl = event.imageUrl;
    this.store.dispatch(
      new AddConfigAsset('backgroundiphoneXappfield', image, displayImageUrl),
    );
  }

  onRemoveBackgroundImage(name: string) {
    this.store.dispatch(new RemoveImage(name));
  }

  onShowHeaderLogoChange(event: boolean) {
    if (event) {
      this.store.dispatch(new UpdateAppConfig(['onglets', '0', 'showHeaderLogo'], 'YES'));
    } else {
      this.store.dispatch(new UpdateAppConfig(['onglets', '0', 'showHeaderLogo'], 'NO'));
    }
  }

  onBlurToggle(event: boolean) {
    if (event) {
      this.store.dispatch(
        new UpdateAppConfig(['onglets', '0', 'blurredBackground'], 'YES'),
      );
    } else {
      this.store.dispatch(
        new UpdateAppConfig(['onglets', '0', 'blurredBackground'], 'NO'),
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
